import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { appDate } from '@/utils/appDateFormat';
import { useSelector } from 'react-redux';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import Select from '@/components/ui/Select';
import { Card, CardContent } from '@/components/ui/Card';
import { PageLayout } from '@/components/ui/PageLayout';
import { Palmtree } from 'lucide-react';
import LeaveTypesTab from './components/LeaveTypesTab';
import EntitlementsAccrualsTab from './components/EntitlementsAccrualsTab';
import ApprovalWorkflowsTab from './components/ApprovalWorkflowsTab';
import CarryForwardTab from './components/CarryForwardTab';
import BlackoutDatesTab from './components/BlackoutDatesTab';
import HolidaysLocationsTab from './components/HolidaysLocationsTab';
import PolicyFormModal from './components/PolicyFormModal';
import {
  getLeavePolicies, createLeavePolicy, updateLeavePolicy,
  getPolicyAudit, exportPolicy, importPolicy, validateStatutoryCompliance
} from '../../../../api/Attendance/leaves';
import type { LeavePolicyView, LeavePolicyInput, PolicyAuditLogView, StatutoryComplianceWarning } from '../../../../models/attendance';
import { todayLocalDateParam } from '@/utils/apiDate';

type AuditActionMeta = {
  label: string;
  icon: string;
  iconClass: string;
  iconBgClass: string;
  bgClass: string;
  borderClass: string;
};

const AUDIT_ACTION_MAP: Record<string, AuditActionMeta> = {
  Created:                { label: 'Policy created',               icon: 'Plus',         iconClass: 'text-emerald-600', iconBgClass: 'bg-emerald-100', bgClass: 'bg-emerald-50/50', borderClass: 'border-emerald-100' },
  Updated:                { label: 'Policy updated',               icon: 'Pencil',       iconClass: 'text-blue-600',    iconBgClass: 'bg-blue-100',    bgClass: 'bg-blue-50/50',    borderClass: 'border-blue-100' },
  Archived:               { label: 'Policy archived',              icon: 'Archive',      iconClass: 'text-amber-600',   iconBgClass: 'bg-amber-100',   bgClass: 'bg-amber-50/50',   borderClass: 'border-amber-100' },
  AccrualRuleCreated:     { label: 'Accrual rule added',           icon: 'Plus',         iconClass: 'text-emerald-600', iconBgClass: 'bg-emerald-100', bgClass: 'bg-emerald-50/50', borderClass: 'border-emerald-100' },
  AccrualRuleUpdated:     { label: 'Accrual rule modified',        icon: 'Pencil',       iconClass: 'text-blue-600',    iconBgClass: 'bg-blue-100',    bgClass: 'bg-blue-50/50',    borderClass: 'border-blue-100' },
  AccrualRuleDeleted:     { label: 'Accrual rule removed',         icon: 'Trash2',       iconClass: 'text-red-600',     iconBgClass: 'bg-red-100',     bgClass: 'bg-red-50/50',     borderClass: 'border-red-100' },
  WorkflowCreated:        { label: 'Approval workflow added',      icon: 'Plus',         iconClass: 'text-emerald-600', iconBgClass: 'bg-emerald-100', bgClass: 'bg-emerald-50/50', borderClass: 'border-emerald-100' },
  WorkflowUpdated:        { label: 'Approval workflow modified',   icon: 'Pencil',       iconClass: 'text-blue-600',    iconBgClass: 'bg-blue-100',    bgClass: 'bg-blue-50/50',    borderClass: 'border-blue-100' },
  WorkflowDeleted:        { label: 'Approval workflow removed',    icon: 'Trash2',       iconClass: 'text-red-600',     iconBgClass: 'bg-red-100',     bgClass: 'bg-red-50/50',     borderClass: 'border-red-100' },
  CarryForwardRuleCreated:{ label: 'Carry-forward rule added',     icon: 'Plus',         iconClass: 'text-emerald-600', iconBgClass: 'bg-emerald-100', bgClass: 'bg-emerald-50/50', borderClass: 'border-emerald-100' },
  CarryForwardRuleUpdated:{ label: 'Carry-forward rule modified',  icon: 'Pencil',       iconClass: 'text-blue-600',    iconBgClass: 'bg-blue-100',    bgClass: 'bg-blue-50/50',    borderClass: 'border-blue-100' },
  CarryForwardRuleDeleted:{ label: 'Carry-forward rule removed',   icon: 'Trash2',       iconClass: 'text-red-600',     iconBgClass: 'bg-red-100',     bgClass: 'bg-red-50/50',     borderClass: 'border-red-100' },
  BlackoutCreated:        { label: 'Blackout period added',        icon: 'Plus',         iconClass: 'text-emerald-600', iconBgClass: 'bg-emerald-100', bgClass: 'bg-emerald-50/50', borderClass: 'border-emerald-100' },
  BlackoutUpdated:        { label: 'Blackout period modified',     icon: 'Pencil',       iconClass: 'text-blue-600',    iconBgClass: 'bg-blue-100',    bgClass: 'bg-blue-50/50',    borderClass: 'border-blue-100' },
  BlackoutDeleted:        { label: 'Blackout period removed',      icon: 'Trash2',       iconClass: 'text-red-600',     iconBgClass: 'bg-red-100',     bgClass: 'bg-red-50/50',     borderClass: 'border-red-100' },
  LeaveTypeCreated:       { label: 'Leave type added',             icon: 'Plus',         iconClass: 'text-emerald-600', iconBgClass: 'bg-emerald-100', bgClass: 'bg-emerald-50/50', borderClass: 'border-emerald-100' },
  LeaveTypeUpdated:       { label: 'Leave type modified',          icon: 'Pencil',       iconClass: 'text-blue-600',    iconBgClass: 'bg-blue-100',    bgClass: 'bg-blue-50/50',    borderClass: 'border-blue-100' },
  LeaveTypeDeleted:       { label: 'Leave type removed',           icon: 'Trash2',       iconClass: 'text-red-600',     iconBgClass: 'bg-red-100',     bgClass: 'bg-red-50/50',     borderClass: 'border-red-100' },
  AssignedToEmployees:    { label: 'Policy assigned to employees', icon: 'Users',        iconClass: 'text-violet-600',  iconBgClass: 'bg-violet-100',  bgClass: 'bg-violet-50/50',  borderClass: 'border-violet-100' },
};

const FALLBACK_META: AuditActionMeta = {
  label: 'Policy change', icon: 'FileText', iconClass: 'text-muted-foreground', iconBgClass: 'bg-muted', bgClass: 'bg-muted/50', borderClass: 'border-border',
};

function getAuditActionMeta(action: string | null | undefined): AuditActionMeta {
  if (!action) return FALLBACK_META;
  return AUDIT_ACTION_MAP[action] ?? FALLBACK_META;
}

function formatRelativeTime(date: Date, t: (key: string, options?: any) => string): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('Just now');
  if (diffMins < 60) return t('{{count}} min ago', { count: diffMins });
  if (diffHours < 24) return t('{{count}} hours ago', { count: diffHours });
  if (diffDays < 7) return t('{{count}} days ago', { count: diffDays });
  return appDate(date);
}

const LeaveSettings: React.FC = () => {
  const { t } = useTranslation('leaves');
  const companyId = useSelector((state: any) => state.user?.company?.id);
  const [activeTab, setActiveTab] = useState<string>('leave-types');
  const [toastMessage, setToastMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const [policies, setPolicies] = useState<LeavePolicyView[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<LeavePolicyView | undefined>();
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(true);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<LeavePolicyView | null>(null);
  const [isSavingPolicy, setIsSavingPolicy] = useState(false);

  const [auditLogs, setAuditLogs] = useState<PolicyAuditLogView[]>([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [complianceWarnings, setComplianceWarnings] = useState<StatutoryComplianceWarning[]>([]);
  const [isCheckingCompliance, setIsCheckingCompliance] = useState(false);

  const showToast = (text: string, type: 'success' | 'error') => {
    setToastMessage({ text, type });
    setTimeout(() => setToastMessage(null), 4000);
  };

  const tabs = [
    { id: 'leave-types', label: t('Leave Types'), icon: 'Calendar' },
    { id: 'entitlements', label: t('Entitlements & Accruals'), icon: 'TrendingUp' },
    { id: 'workflows', label: t('Approval Workflows'), icon: 'GitBranch' },
    { id: 'carry-forward', label: t('Carry-Forward Rules'), icon: 'RotateCcw' },
    { id: 'blackout-dates', label: t('Blackout Dates'), icon: 'XCircle' },
    { id: 'holidays', label: t('Holidays'), icon: 'MapPin' }
  ];

  useEffect(() => {
    loadPolicies();
  }, []);

  useEffect(() => {
    if (selectedPolicy?.id) {
      loadAudit(selectedPolicy.id);
      loadCompliance(selectedPolicy.id);
    }
  }, [selectedPolicy?.id]);

  const loadPolicies = async () => {
    setIsLoadingPolicies(true);
    try {
      const result = await getLeavePolicies();
      if (result?.errors?.length) return;
      const list = result.payload ?? [];
      setPolicies(list);
      setSelectedPolicy(list[0]);
    } finally {
      setIsLoadingPolicies(false);
    }
  };

  const loadAudit = async (policyId: number) => {
    setIsLoadingAudit(true);
    try {
      const result = await getPolicyAudit(policyId);
      if (!result?.errors?.length) {
        setAuditLogs(result.payload ?? []);
      }
    } finally {
      setIsLoadingAudit(false);
    }
  };

  const loadCompliance = async (policyId: number) => {
    setIsCheckingCompliance(true);
    try {
      const result = await validateStatutoryCompliance(policyId);
      if (!result?.errors?.length) {
        setComplianceWarnings(result.payload ?? []);
      }
    } finally {
      setIsCheckingCompliance(false);
    }
  };

  const handlePolicyChange = (policyId: number) => {
    const policy = policies.find(p => p.id === policyId);
    setSelectedPolicy(policy);
  };

  const handleAddPolicy = () => {
    setEditingPolicy(null);
    setShowPolicyModal(true);
  };

  const handleEditPolicy = () => {
    setEditingPolicy(selectedPolicy ?? null);
    setShowPolicyModal(true);
  };

  const handleSavePolicy = async (input: LeavePolicyInput) => {
    setIsSavingPolicy(true);
    try {
      if (editingPolicy) {
        const result = await updateLeavePolicy(editingPolicy.id, input);
        if (result?.errors?.length) {
          const msg = result.errors[0]?.description || result.errors[0]?.message;
          showToast(msg || t('translation:somethingWentWrong', 'Failed to update policy'), 'error');
          return;
        }
        const updated = result.payload!;
        setPolicies(prev => prev.map(p => p.id === editingPolicy.id ? updated : p));
        setSelectedPolicy(updated);
      } else {
        const result = await createLeavePolicy(input);
        if (result?.errors?.length) {
          const msg = result.errors[0]?.description || result.errors[0]?.message;
          showToast(msg || t('translation:somethingWentWrong', 'Failed to create policy'), 'error');
          return;
        }
        const created = result.payload!;
        setPolicies(prev => [...prev, created]);
        setSelectedPolicy(created);
      }
      setShowPolicyModal(false);
      setEditingPolicy(null);
    } finally {
      setIsSavingPolicy(false);
    }
  };

  const handleExportSettings = async () => {
    if (!selectedPolicy) return;
    try {
      const result = await exportPolicy(selectedPolicy.id);
      if (result?.errors?.length) return;
      const json = result.payload ?? '';
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leave-policy-${selectedPolicy.name}-${todayLocalDateParam()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      showToast(t('Export failed'), 'error');
    }
  };

  const handleImportSettings = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e: Event) => {
      const file = (e.target as HTMLInputElement)?.files?.[0];
      if (!file) return;
      const text = await file.text();
      try {
        const result = await importPolicy(text);
        if (result?.errors?.length) return;
        await loadPolicies();
      } catch (err) {
        console.error('Import failed:', err);
        showToast(t('Import failed'), 'error');
      }
    };
    input.click();
  };

  return (
    <PageLayout
      title={t('Leave Settings & Policies')}
      description={t('Configure leave types, entitlements, approval workflows, and organizational policies')}
      icon={Palmtree}
      accentColor="emerald"
      className="min-h-screen bg-background"
      headerActions={(
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" iconName="Download" onClick={handleExportSettings} data-testid="export-settings-btn">
            {t('Export')}
          </Button>
          <Button variant="outline" size="sm" iconName="Upload" onClick={handleImportSettings} data-testid="import-settings-btn">
            {t('Import')}
          </Button>
        </div>
      )}
    >
      <div className="space-y-6">
          {/* Leave Policy Selector */}
          <Card variant="elevated" className="mb-6">
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Icon name="Shield" size={20} className="text-primary" />
                  <h3 className="text-lg font-semibold text-foreground">{t('Leave Policy')}</h3>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" iconName="Edit" onClick={handleEditPolicy} disabled={!selectedPolicy} data-testid="edit-policy-btn">
                    {t('Edit Policy')}
                  </Button>
                  <Button variant="ghost" size="sm" iconName="Plus" onClick={handleAddPolicy} data-testid="new-policy-btn">
                    {t('New Policy')}
                  </Button>
                </div>
              </div>

              {isLoadingPolicies ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground" data-testid="loading-policies">
                  <Icon name="Loader2" size={20} className="animate-spin me-2" />
                  {t('Loading policies...')}
                </div>
              ) : policies.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="empty-policies">
                  <Icon name="FileText" size={40} className="mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-sm">{t('No policies found. Create your first leave policy.')}</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  <div data-testid="policy-selector">
                    <label className="block text-sm font-medium text-foreground mb-2">{t('Select Policy')}</label>
                    <Select
                      value={selectedPolicy?.id}
                      onChange={(value: number) => handlePolicyChange(value)}
                      options={policies.map(p => ({
                        value: p.id,
                        label: `${p.name}${p.isDefault ? ` (${t('Default')})` : ''}`
                      }))}
                    />
                  </div>
                  <div className="flex flex-col justify-end">
                    <div className="text-sm text-muted-foreground">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="Building2" size={14} />
                        <span><strong>{t('Scope')}:</strong> {selectedPolicy?.legalEntity || '\u2014'} &bull; {selectedPolicy?.country || '\u2014'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Users" size={14} />
                        <span><strong>{t('Employment Type')}:</strong> {selectedPolicy?.employmentType || '\u2014'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedPolicy?.description && (
                <div className="mt-3 p-3 bg-muted rounded-md text-sm text-foreground">
                  <Icon name="Info" size={14} className="inline me-2" />
                  {selectedPolicy.description}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tab Navigation with shared Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <Card variant="elevated" className="mb-6">
              <TabsList className="flex flex-col sm:flex-row sm:overflow-x-auto sm:space-x-1 rtl:sm:space-x-reverse space-y-1 sm:space-y-0 p-1 border-b border-border w-full bg-transparent rounded-none">
                {tabs.map((tab) => (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    data-testid={`tab-${tab.id}`}
                    className="flex items-center space-x-2 rtl:space-x-reverse min-h-[44px] whitespace-nowrap"
                  >
                    <Icon name={tab.icon} size={16} />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="p-6">
                <TabsContent value="leave-types">
                  <LeaveTypesTab currentPolicy={selectedPolicy} />
                </TabsContent>
                <TabsContent value="entitlements">
                  <EntitlementsAccrualsTab currentPolicy={selectedPolicy} />
                </TabsContent>
                <TabsContent value="workflows">
                  <ApprovalWorkflowsTab currentPolicy={selectedPolicy} />
                </TabsContent>
                <TabsContent value="carry-forward">
                  <CarryForwardTab currentPolicy={selectedPolicy} />
                </TabsContent>
                <TabsContent value="blackout-dates">
                  <BlackoutDatesTab currentPolicy={selectedPolicy} />
                </TabsContent>
                <TabsContent value="holidays">
                  <HolidaysLocationsTab currentPolicy={selectedPolicy} />
                </TabsContent>
              </div>
            </Card>
          </Tabs>

          {/* Compliance & Audit Trail Section */}
          <Card variant="elevated" data-testid="audit-trail-section">
            <CardContent>
              <div className="flex items-center space-x-3 rtl:space-x-reverse mb-4">
                <Icon name="Shield" size={20} className="text-muted-foreground" />
                <h3 className="text-lg font-semibold text-foreground">{t('Compliance & Recent Changes')}</h3>
              </div>

              {/* Statutory Compliance Warnings */}
              {selectedPolicy && (
                <div className="mb-6" data-testid="compliance-section">
                  <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Scale" size={14} className="text-warning" />
                    {t('Compliance Warnings')}
                    {isCheckingCompliance && <Icon name="Loader2" size={12} className="animate-spin text-muted-foreground" />}
                  </h4>
                  {complianceWarnings.length === 0 ? (
                    <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg border border-success/20 mb-4">
                      <Icon name="CheckCircle2" size={16} className="text-success" />
                      <span className="text-sm text-success">{t('complianceNoWarnings')}</span>
                    </div>
                  ) : (
                    <div className="space-y-2 mb-4">
                      {complianceWarnings.map((w, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-warning/10 rounded-lg border border-warning/20">
                          <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5 shrink-0" />
                          <div className="text-sm">
                            <p className="font-medium text-warning">{w.leaveTypeName}</p>
                            <p className="text-warning">
                              {t('statutoryMinimum')}: <strong>{w.statutoryMinDays}</strong> &bull; {t('configured')}: <strong>{w.configuredDays}</strong>
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div>
                <h4 className="text-sm font-medium text-foreground mb-3">{t('Recent Policy Changes')}</h4>
                {isLoadingAudit ? (
                  <div className="flex items-center justify-center py-4 text-muted-foreground">
                    <Icon name="Loader2" size={16} className="animate-spin me-2" />
                    {t('Loading...')}
                  </div>
                ) : auditLogs.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">{t('No changes recorded yet')}</p>
                ) : (
                  <div className="space-y-1.5">
                    {auditLogs.slice(0, 10).map((log) => {
                      const meta = getAuditActionMeta(log.action);
                      return (
                        <div key={log.id} className={`flex items-start gap-2.5 rtl:gap-reverse p-2.5 rounded-lg border ${meta.bgClass} ${meta.borderClass}`}>
                          <div className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${meta.iconBgClass}`}>
                            <Icon name={meta.icon} size={13} className={meta.iconClass} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-foreground">{t(meta.label)}</p>
                            {log.changeDetails && (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">{log.changeDetails}</p>
                            )}
                            <div className="flex items-center gap-1.5 mt-1 text-[11px] text-muted-foreground">
                              {log.changedByName && (
                                <>
                                  <Icon name="User" size={10} />
                                  <span>{log.changedByName}</span>
                                  <span>&middot;</span>
                                </>
                              )}
                              <span>{formatRelativeTime(new Date(log.changedAt), t)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg text-sm font-medium z-50 ${
          toastMessage.type === 'success' ? 'bg-success text-success-foreground' : 'bg-destructive text-destructive-foreground'
        }`}>
          {toastMessage.text}
        </div>
      )}

      {/* Policy Modal */}
      <PolicyFormModal
        isOpen={showPolicyModal}
        onClose={() => {
          setShowPolicyModal(false);
          setEditingPolicy(null);
        }}
        editingPolicy={editingPolicy}
        onSave={handleSavePolicy}
        isSaving={isSavingPolicy}
        companyId={companyId}
      />
    </PageLayout>
  );
};

export default LeaveSettings;
